import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { signIn, resetPassword, confirmResetPassword } from '@aws-amplify/auth';
import styles from './LoginPopup.module.css';

interface LoginPopupProps {
  show: boolean;
  onHide: () => void;
  onLogin: () => void;
}

const LoginPopup: React.FC<LoginPopupProps> = ({ show, onHide, onLogin }) => {
  const [step, setStep] = useState<'login' | 'requestReset' | 'resetPassword' | 'success'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmationCode: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const resetState = () => {
    setStep('login');
    setLoading(false);
    setError(null);
    setFormData({
      email: '',
      password: '',
      confirmationCode: '',
      newPassword: '',
      confirmNewPassword: ''
    });
  };

  useEffect(() => {
    if (show) {
      resetState();
      
      // Clear all Cognito-related localStorage entries
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('CognitoIdentityServiceProvider')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
    }
  }, [show]);

  const validateLoginForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateResetForm = () => {
    if (!formData.confirmationCode || !formData.newPassword || !formData.confirmNewPassword) {
      setError('All fields are required');
      return false;
    }
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateLoginForm()) return;

    setLoading(true);
    try {
      await signIn({
        username: formData.email,
        password: formData.password
      });
      onLogin();
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await resetPassword({
        username: formData.email
      });
      setStep('resetPassword');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while requesting password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateResetForm()) return;

    setLoading(true);
    try {
      await confirmResetPassword({
        username: formData.email,
        confirmationCode: formData.confirmationCode,
        newPassword: formData.newPassword
      });
      await signIn({
        username: formData.email,
        password: formData.newPassword
      });
      setStep('success');
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during password reset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {step === 'login' ? 'Login' : 
           step === 'requestReset' ? 'Reset Password' : 
           step === 'resetPassword' ? 'Enter Reset Code' : 
           'Success!'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === 'login' ? (
          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
              />
            </Form.Group>
            {error && <div className={styles.error}>{error}</div>}
            <Button variant="primary" type="submit" disabled={loading} className="w-100 mb-2">
              {loading ? <Spinner animation="border" size="sm" /> : 'Login'}
            </Button>
            <Button
              variant="link"
              onClick={() => {
                setError(null);
                setStep('requestReset');
              }}
              disabled={loading}
              className="w-100"
            >
              Forgot password?
            </Button>
          </Form>
        ) : step === 'requestReset' ? (
          <Form onSubmit={handleRequestReset}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
              />
            </Form.Group>
            {error && <div className={styles.error}>{error}</div>}
            <Button variant="primary" type="submit" disabled={loading} className="w-100">
              {loading ? <Spinner animation="border" size="sm" /> : 'Send Reset Code'}
            </Button>
          </Form>
        ) : step === 'resetPassword' ? (
          <Form onSubmit={handleResetPassword}>
            <p>We've sent a reset code to:</p>
            <div className={styles.emailHighlight}>{formData.email}</div>
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Reset Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.confirmationCode}
                onChange={(e) => setFormData({ ...formData, confirmationCode: e.target.value })}
                placeholder="Enter reset code"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="Enter new password"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.confirmNewPassword}
                onChange={(e) => setFormData({ ...formData, confirmNewPassword: e.target.value })}
                placeholder="Confirm new password"
              />
            </Form.Group>
            {error && <div className={styles.error}>{error}</div>}
            <Button variant="primary" type="submit" disabled={loading} className="w-100">
              {loading ? <Spinner animation="border" size="sm" /> : 'Reset Password'}
            </Button>
          </Form>
        ) : (
          <div className={styles.successContainer}>
            <i className={`bi bi-check-circle-fill ${styles.successIcon}`}></i>
            <h4 className={styles.successTitle}>Password Reset Successful!</h4>
            <p className={styles.successMessage}>
              Your password has been reset and you are now logged in.
            </p>
            <Button variant="primary" onClick={onHide} className="w-100">
              Continue
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LoginPopup; 